<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import subtotal from "./../subtotal.vue";
import { getServices, checkNoonConflict } from "@/api/api";
import { useBookingDataStore } from "@/stores/bookingData";
import { itemsAmount } from "@/api/types";
import { mapItemToBookingItem } from "@/services/price";
import { buildLogementBooking } from "@/services/lodging";
import { canLeaveTypeStep } from "@/services/venue-gate";
import { useTextStore } from "@/stores/texts";
import { formatDateGSheet, nextDayStr } from "@/utils.ts/date";

const bookingData = useBookingDataStore();
const textStore = useTextStore();

// Phased rollout of La Serre — remove once both services are live everywhere.
// Month is 0-indexed: 4 = May, 5 = June.
const GREENHOUSE_DINER_START = new Date(2026, 4, 13);
const GREENHOUSE_LOGEMENT_START = new Date(2026, 5, 17);

// No *implicit* default venue: the customer must explicitly pick La Salle or
// La Serre — either via the date step's venue filter (carried into
// bookingData.venue and re-validated below on mount) or on this step — before
// leaving (see canLeaveTypeStep). An implicit default once booked La Table on
// a serre-only date (2026-06-12 double booking).
const selectedVenue = ref<'lasalle' | 'serre' | ''>('');
const selectedDiner = ref("dinerlatable");
const selectedDisposition = ref("dinerlasalleovale");
const manualSeparateTables = ref(false);
const amount = ref<itemsAmount>({});
const combinedSpaces = computed(() => selectedVenue.value === 'lasalle' && amount.value['diner'] > salleMaxValue.value);
const services = ref(Array());
const TEXT_infotype = ref("");
const combinedSpacesTemplate = ref("");
const combinedSpacesMessage = computed(() => {
  const values: Record<string, string | number> = {
    salle: salleMaxValue.value,
    roue: amount.value['diner'] - salleMaxValue.value,
    roueMaximum: roueMaxValue.value,
    sallePrice: getService('dinerlasalle')?.Prix ?? '',
    rouePrice: getService('dinerlatable')?.Prix ?? '',
  };
  return combinedSpacesTemplate.value.replace(/\{(\w+)\}/g, (placeholder, key) => String(values[key] ?? placeholder));
});

// Diner min/max based on selected venue
// Missing limits disable the affected choice until its product is configured.
function serviceMinimum(id: string): number {
  const value = Number(getService(id)?.Minimum);
  return Number.isInteger(value) && value > 0 ? value : Infinity;
}
function serviceMaximum(id: string): number {
  const value = Number(getService(id)?.Maximum);
  return Number.isInteger(value) && value > 0 ? value : 0;
}
const salleMinValue = computed(() => serviceMinimum('dinerlasalle'));
const salleMaxValue = computed(() => Math.min(serviceMaximum('dinerlasalle'), serviceMaximum('dinerlasalletables')));
const roueMaxValue = computed(() => serviceMaximum('dinerlatable'));
const ovalAvailable = computed(() => amount.value['diner'] >= Math.max(salleMinValue.value, serviceMinimum('dinerlasalleovale'))
  && amount.value['diner'] <= Math.min(serviceMaximum('dinerlasalle'), serviceMaximum('dinerlasalleovale')));
const separateTablesAvailable = computed(() => amount.value['diner'] >= Math.max(salleMinValue.value, serviceMinimum('dinerlasalletables'))
  && (combinedSpaces.value || amount.value['diner'] <= salleMaxValue.value));
const dinerMinValue = computed(() => serviceMinimum('diner'));
const dinerMaxValue = computed(() => {
  if (selectedVenue.value === 'serre') {
    return Math.min(serviceMaximum('diner'), serviceMaximum('dinerlaserre'));
  }
  return Math.min(serviceMaximum('diner'), salleMaxValue.value + roueMaxValue.value);
});

// Venue picker semantics switch between diner and lodging when only one is picked.
const venueMode = computed<'diner' | 'logement'>(() =>
  amount.value['diner'] > 0 ? 'diner' : 'logement'
);

const venueHeading = computed(() => {
  const hasDiner = amount.value['diner'] > 0;
  const hasLogement = amount.value['logement'] > 0;
  if (hasDiner && hasLogement) return 'Lieu du dîner et du logement';
  if (hasDiner) return 'Lieu du dîner';
  return 'Lieu du logement';
});

// Lowest Prix among a set of services, preserving the original formatting of the winner.
function parsePriceNumber(raw: string | undefined): number {
  if (!raw) return NaN;
  return parseFloat(raw.replace(/,/g, '.').replace(/[^0-9.]/g, ''));
}

function lowestAvailablePrix(serviceIds: string[]): string {
  const entries = serviceIds
    .map(id => getService(id)?.Prix)
    .filter((p): p is string => !!p)
    .map(raw => ({ raw, num: parsePriceNumber(raw) }))
    .filter(p => !isNaN(p.num));
  if (entries.length === 0) return '';
  return entries.reduce((min, p) => (p.num < min.num ? p : min)).raw;
}

// Main card prices show "à partir de <lowest>" across the venues available on the selected date.
const dinerCardPrice = computed(() => {
  const ids: string[] = [];
  if (venueAvailable('lasalle')) ids.push('dinerlatable', 'dinerlasalle');
  if (venueAvailable('serre')) ids.push('dinerlaserre');
  const lowest = lowestAvailablePrix(ids);
  return 'à partir de ' + (lowest || getService('diner')?.Prix || '');
});

const logementCardPrice = computed(() => {
  // When diner has locked the venue, show the exact venue-specific logement price
  // rather than "à partir de" (no longer a range).
  if (amount.value['diner'] > 0 && selectedVenue.value === 'serre') {
    const exact = getService('logement_serre')?.Prix;
    if (exact) return exact;
  }
  if (amount.value['diner'] > 0 && selectedVenue.value === 'lasalle') {
    const exact = getService('logement')?.Prix;
    if (exact) return exact;
  }
  const ids: string[] = [];
  if (venueAvailable('lasalle')) ids.push('logement');
  if (venueAvailable('serre')) ids.push('logement_serre');
  const lowest = lowestAvailablePrix(ids);
  return 'à partir de ' + (lowest || getService('logement')?.Prix || '');
});

// When a diner is also selected, the top logement card reflects the venue-specific
// logement service (logement_roue for La Salle, logement_serre for La Serre). Without
// a diner, the venue is not yet locked — keep the generic `logement` title/description/photo.
const logementCardTitle = computed(() => {
  if (amount.value['diner'] > 0 && selectedVenue.value === 'serre') {
    return getService('logement_serre')?.Titre ?? getService('logement')?.Titre ?? '';
  }
  if (amount.value['diner'] > 0 && selectedVenue.value === 'lasalle') {
    return getService('logement_roue')?.Titre ?? getService('logement')?.Titre ?? '';
  }
  return getService('logement')?.Titre ?? '';
});

const logementCardDescription = computed(() => {
  if (amount.value['diner'] > 0 && selectedVenue.value === 'serre') {
    return getService('logement_serre')?.Description
      ?? getService('logement')?.Description
      ?? '';
  }
  if (amount.value['diner'] > 0 && selectedVenue.value === 'lasalle') {
    return getService('logement_roue')?.Description
      ?? getService('logement')?.Description
      ?? '';
  }
  return getService('logement')?.Description ?? '';
});

const logementCardPicture = computed(() => {
  if (amount.value['diner'] > 0 && selectedVenue.value === 'serre') {
    return getService('logement_serre')?.Photo ?? getService('logement')?.Photo;
  }
  if (amount.value['diner'] > 0 && selectedVenue.value === 'lasalle') {
    return getService('logement_roue')?.Photo ?? getService('logement')?.Photo;
  }
  return getService('logement')?.Photo;
});

// Venue-card prices adapt to which service drives the choice.
const salleVenuePrice = computed(() => {
  if (venueMode.value === 'logement') {
    return getService('logement')?.Prix ?? '';
  }
  return 'à partir de ' + (getService('dinerlatable')?.Prix || getService('dinerlasalle')?.Prix || '');
});

const serreVenuePrice = computed(() => {
  if (venueMode.value === 'logement') {
    return getService('logement_serre')?.Prix ?? '';
  }
  return getService('dinerlaserre')?.Prix ?? '';
});

const salleVenueDescription = computed(() => {
  if (!venueAvailable('lasalle')) return 'Indisponible à cette date';
  if (venueMode.value === 'logement') {
    return getService('logement_roue')?.Description
      ?? getService('logement')?.Description
      ?? '';
  }
  return 'Dîner dans la salle ou à la table';
});

// In logement-only mode, the venue cards take their titles from the placeholder
// logement services so they can carry venue-branded naming from the sheet.
const salleVenueTitle = computed(() => {
  if (venueMode.value === 'logement') {
    return getService('logement_roue')?.Titre ?? 'La Salle / Table';
  }
  return 'La Salle / Table';
});

const serreVenueTitle = computed(() => {
  if (venueMode.value === 'logement') {
    return getService('logement_serre')?.Titre ?? 'La Serre';
  }
  return 'La Serre';
});

// Venue sub-picker photos track the active mode: diner photos when a diner is being
// chosen, venue-specific logement photos when only a logement is in play.
const salleVenuePicture = computed(() => {
  if (venueMode.value === 'logement') {
    return getService('logement_roue')?.Photo ?? getService('logement')?.Photo;
  }
  return getService('dinerlasalle')?.Photo || getService('dinerlatable')?.Photo;
});

const serreVenuePicture = computed(() => {
  if (venueMode.value === 'logement') {
    return getService('logement_serre')?.Photo ?? getService('dinerlaserre')?.Photo;
  }
  return getService('dinerlaserre')?.Photo || getService('logement_serre')?.Photo;
});

// Phased rollout: describe why La Serre is blocked for the current selection +
// date, or return '' when it is actually bookable. Used by both the disabled
// flag and the card description.
const serreBlockReason = computed<string>(() => {
  if (!venueAvailable('serre')) return 'Indisponible à cette date';
  const d = bookingData.date;
  if (!d) return '';
  const hasDiner = amount.value['diner'] > 0;
  const hasLogement = amount.value['logement'] > 0;
  if (hasLogement && d < GREENHOUSE_LOGEMENT_START) {
    return 'Logement à La Serre disponible à partir du 17 juin 2026';
  }
  if (hasDiner && d < GREENHOUSE_DINER_START) {
    return 'Dîner à La Serre disponible à partir du 13 mai 2026';
  }
  return '';
});

const serreDisabled = computed(() => serreBlockReason.value !== '');

// The top-row Logement card is only actionable if at least one venue can host
// a logement booking on the chosen date. Before June 17 2026 La Serre cannot,
// so if La Salle is unavailable the whole logement service is off the table.
const logementSelectable = computed(() => {
  const d = bookingData.date;
  const lasalleOK = venueAvailable('lasalle');
  const serreOK = venueAvailable('serre') && !!d && d >= GREENHOUSE_LOGEMENT_START;
  return lasalleOK || serreOK;
});

const serreVenueDescription = computed(() => {
  const reason = serreBlockReason.value;
  if (reason) return reason;
  if (venueMode.value === 'logement') {
    return getService('logement_serre')?.Description
      ?? getService('dinerlaserre')?.Description
      ?? '';
  }
  return getService('dinerlaserre')?.Description ?? '';
});

onMounted(async () => {
  const date = bookingData.date;
  if (date == undefined) {
    throw new Error('no date set');
  }
  const s = await getServices(date);
  if (s?.success && s.data) {
    services.value = s.data;
    services.value.forEach((o) => {
      amount.value[o.Id] = 0;
    });

    // Load current data
    if (Array.isArray(bookingData.services)) {
      bookingData.services.forEach((s) => {
        amount.value[s.id] = s.quantity;
        if (s.id == "diner" && s.quantity > 0) {
          // Restore venue and sub-selections from saved booking
          if (s.options[0]?.id === 'dinerlaserre') {
            selectedVenue.value = 'serre';
            bookingData.venue = 'serre';
          } else {
            selectedVenue.value = 'lasalle';
            bookingData.venue = 'lasalle';
            selectedDiner.value = s.options[0]?.id ?? 'dinerlatable';
            selectedDisposition.value = s.options.find(o =>
              ['dinerlasalleovale', 'dinerlasalletables'].includes(o.id)
            )?.id ?? 'dinerlasalleovale';
            manualSeparateTables.value = ovalAvailable.value
              && selectedDisposition.value === 'dinerlasalletables';
          }
        }
      });
    }

    // Restore venue from store if not set from services
    if (selectedVenue.value === '' && bookingData.venue !== '') {
      selectedVenue.value = bookingData.venue;
    }

    // If the restored venue is no longer selectable for the current date +
    // service combo (backend availability or phased-rollout block), switch
    // to the other venue when possible — or clear the selection entirely
    // so we never render a greyed-out card as "selected".
    if (selectedVenue.value !== '' && !venueSelectable(selectedVenue.value)) {
      const other = selectedVenue.value === 'lasalle' ? 'serre' : 'lasalle';
      if (venueSelectable(other)) {
        selectVenue(other);
      } else {
        selectedVenue.value = '';
        bookingData.venue = '';
      }
    }
  }
  TEXT_infotype.value = (await textStore.getText("infotype")) || "";
  combinedSpacesTemplate.value = (await textStore.getText("combined_spaces_info")) || "";
});

// React to the user toggling services on/off (or the date changing) — if
// La Serre was previously selected but is now blocked by the phased-rollout
// rules, switch to La Salle automatically so the wizard never sits in an
// invalid state.
watch(serreBlockReason, (reason) => {
  if (!reason || selectedVenue.value !== 'serre') return;
  if (venueSelectable('lasalle')) {
    selectVenue('lasalle');
  } else {
    selectedVenue.value = '';
    bookingData.venue = '';
  }
});

function toggleType(type: string) {
  if (amount.value[type] !== 0) return;
  if (type === 'logement' && !logementSelectable.value) return;
  if (type === 'diner' && (!Number.isFinite(dinerMinValue.value) || dinerMaxValue.value < dinerMinValue.value)) return;
  amount.value[type] = type === 'diner' ? dinerMinValue.value : 2;
}

function venueAvailable(venue: string): boolean {
  const dateStr = formatDateGSheet(bookingData.date);
  if (dateStr === '--') return false;
  const venues = bookingData.venuesByDate[dateStr];
  if (!venues) return true; // default: available if no venue data
  return venues.includes(venue);
}

// True when the venue can actually host the user's current selection — combines
// the backend availability flag with the phased-rollout block. Used by the
// auto-switch fallbacks so they don't silently land on a non-selectable target.
function venueSelectable(venue: 'lasalle' | 'serre'): boolean {
  if (!venueAvailable(venue)) return false;
  if (venue === 'serre' && serreBlockReason.value) return false;
  return true;
}

function selectVenue(venue: 'lasalle' | 'serre') {
  if (!venueSelectable(venue)) return;
  selectedVenue.value = venue;
  bookingData.venue = venue;
  manualSeparateTables.value = false;

  if (venue === 'serre') {
    // Clear lasalle sub-selections
    selectedDiner.value = 'dinerlatable';
    selectedDisposition.value = 'dinerlasalletables';
    // Enforce greenhouse max
    if (amount.value['diner'] > dinerMaxValue.value) {
      amount.value['diner'] = dinerMaxValue.value;
    }
    if (amount.value['diner'] > 0) {
      updateGreenhouseDinerBooking();
    }
  } else {
    // Venue is lasalle — restore appropriate sub-selection based on party size
    if (amount.value['diner'] >= salleMinValue.value) {
      selectedDiner.value = 'dinerlasalle';
    } else {
      selectedDiner.value = 'dinerlatable';
    }
    updateDisposition();
    if (amount.value['diner'] > 0) {
      updateDinerBooking();
    }
  }

  // Re-trigger logement update to apply correct pricing
  if (amount.value['logement'] > 0) {
    updateLogementBooking();
  }
}

function selectDiner(type: string) {
  if (
    (!combinedSpaces.value && amount.value["diner"] >= serviceMinimum("dinerlatable") && amount.value["diner"] <= roueMaxValue.value && type == "dinerlatable") ||
    (amount.value["diner"] >= salleMinValue.value && type == "dinerlasalle")
  ) {
    selectedDiner.value = type;
  }
  updateDinerBooking();
}

function getService(id: string) {
  return services.value.find((s) => s.Id == id);
}

function selectDisposition(type: string) {
  if (
    (type === "dinerlasalleovale" && ovalAvailable.value) ||
    (type === "dinerlasalletables" && separateTablesAvailable.value)
  ) {
    selectedDisposition.value = type;
    manualSeparateTables.value = type === 'dinerlasalletables';
  }
  updateDinerBooking();
}

function updateGreenhouseDinerBooking() {
  const serreService = getService('dinerlaserre');
  if (!serreService) return;

  const serreOption = mapItemToBookingItem(serreService, 1);
  // Build a dummy disposition option (not applicable for greenhouse, but keeps the data shape consistent)
  const noDisposition = { id: 'none', activated: false, quantity: 0, packagePrice: '0', unitPrice: '0', options: [], meta: {} };

  const dinerBooking = mapItemToBookingItem(
    getService("diner"),
    amount.value["diner"],
    [serreOption, noDisposition]
  );
  dinerBooking.packagePrice = serreOption.packagePrice;

  bookingData.updateService(dinerBooking);
}

function updateDisposition() {
  if (!ovalAvailable.value) manualSeparateTables.value = false;
  selectedDisposition.value = !combinedSpaces.value && ovalAvailable.value && !manualSeparateTables.value
    ? 'dinerlasalleovale' : 'dinerlasalletables';
}

function updateDinerBooking() {
  if (combinedSpaces.value) {
    selectedDiner.value = "dinerlasalle";
    selectedDisposition.value = "dinerlasalletables";
  }
  const dinerOption = mapItemToBookingItem(getService(selectedDiner.value), 1);
  const dispositionOption = mapItemToBookingItem(
    getService(selectedDisposition.value),
    1
  );

  const options = [dinerOption, dispositionOption];
  if (combinedSpaces.value) {
    options.push(mapItemToBookingItem(getService('dinerlatable'), 1));
  }
  const services = mapItemToBookingItem(
    getService("diner"),
    amount.value["diner"],
    options
  );
  services.packagePrice = combinedSpaces.value
    ? (parsePriceNumber(dinerOption.packagePrice) + parsePriceNumber(options[2].packagePrice)).toFixed(2)
    : dinerOption.packagePrice;

  bookingData.updateService(services);
}

function updateLogementBooking() {
  const logementBooking = buildLogementBooking(getService, amount.value['logement'], selectedVenue.value);
  if (logementBooking) bookingData.updateService(logementBooking);
}

watch(
  () => amount.value["diner"],
  async (curr, prev) => {
    if (curr > dinerMaxValue.value) {
      amount.value['diner'] = dinerMaxValue.value;
      return;
    }
    if (selectedVenue.value === 'serre') {
      updateGreenhouseDinerBooking();
      return;
    }

    // Default to La Salle when it becomes available; preserve valid manual choices.
    if (combinedSpaces.value || curr > roueMaxValue.value || (curr >= salleMinValue.value && prev !== undefined && prev < salleMinValue.value)) {
      selectedDiner.value = "dinerlasalle";
    }
    if (curr < salleMinValue.value) {
      selectedDiner.value = "dinerlatable";
    }

    updateDisposition();

    updateDinerBooking();
  }
);

function canGoNext() {
  const diners = amount.value['diner'] ?? 0;
  if (diners > 0) {
    if (diners < dinerMinValue.value || diners > dinerMaxValue.value) return false;
    if (selectedVenue.value === 'lasalle') {
      if (selectedDiner.value === 'dinerlatable' &&
          (diners < serviceMinimum('dinerlatable') || diners > roueMaxValue.value)) return false;
      if (selectedDiner.value === 'dinerlasalle' &&
          (selectedDisposition.value === 'dinerlasalleovale' ? !ovalAvailable.value : !separateTablesAvailable.value)) return false;
    }
    if (selectedVenue.value === 'serre' && diners < serviceMinimum('dinerlaserre')) return false;
  }
  return canLeaveTypeStep({
    dinerQuantity: amount.value["diner"] ?? 0,
    logementQuantity: amount.value["logement"] ?? 0,
    venue: selectedVenue.value,
  });
}

watch(
  () => amount.value["logement"],
  async (curr, prev) => {
    updateLogementBooking();
  }
);

// Early, non-blocking heads-up: the moment lodging is added on a date whose
// morning-after (D+1) already has a noon service on the SAME physical side
// (la salle/la table share a side; la serre is independent), tell the customer
// the logement must be vacated early. The binding acknowledgment still happens
// on the summary step — this only avoids a late surprise. Scoped by venue so a
// serre noon event never warns a la salle/la table stay.
const noonNotice = ref(false);
async function refreshNoonNotice() {
  const d = bookingData.date;
  const venue = selectedVenue.value;
  if (!d || venue === '' || !(amount.value['logement'] > 0)) {
    noonNotice.value = false;
    return;
  }
  const result = await checkNoonConflict(nextDayStr(d), venue);
  noonNotice.value = !!(result?.success && result.data?.hasConflict);
}
watch(
  [() => amount.value['logement'], selectedVenue, () => bookingData.date],
  refreshNoonNotice,
  { immediate: true },
);
</script>

<template>
  <div
    class="bpa-front-tabs--panel-body __bpa-is-active eauget-booking-step-type"
    data-testid="step-type-panel"
  >
    <div class="bpa-front-default-card">
      <!---->
      <div class="bpa-front-dc--body">
        <!---->
        <div class="el-row">
          <div
            class="el-col el-col-24 el-col-xs-24 el-col-sm-24 el-col-md-24 el-col-lg-24 el-col-xl-24"
          >
            <div
              v-if="services.length > 0"
              class="bpa-front-module-container bpa-front-module--service"
              data-testid="service-selection"
            >
              <div class="bpa-front-module-heading">Votre réservation</div>
              <p class="infotext">{{ TEXT_infotype }}</p>
              <div class="bpa-front-module--service-items-row">
                <card-item
                  :title="getService('diner').Titre"
                  :description="getService('diner').Description"
                  priceLabel="Découvrez l'expérience"
                  :price="dinerCardPrice"
                  :pictureUrl="getService('diner').Photo"
                  v-model="amount[getService('diner').Id]"
                  :isSelected="amount[getService('diner').Id] > 0"
                  :hasQuantity="true"
                  quantityLabel="Personnes"
                  :minValue="dinerMinValue"
                  :canAdd="Number.isFinite(dinerMinValue) && dinerMaxValue >= dinerMinValue"
                  :maxValue="dinerMaxValue"
                  @click="toggleType(getService('diner').Id)"
                ></card-item>
                <card-item
                  :title="logementCardTitle"
                  :description="logementCardDescription"
                  priceLabel="Découvrez l'expérience"
                  :price="logementCardPrice"
                  :pictureUrl="logementCardPicture"
                  v-model="amount[getService('logement').Id]"
                  :isSelected="amount[getService('logement').Id] > 0"
                  :disabled="!logementSelectable"
                  :canAdd="logementSelectable"
                  :hasQuantity="true"
                  quantityLabel="Personnes"
                  :minValue="2"
                  :maxValue="getService('logement').Maximum"
                  @click="toggleType(getService('logement').Id)"
                ></card-item>
              </div>
              <!---- Venue selector (shown when at least one service picked) -->
              <span v-if="amount[getService('diner').Id] > 0 || amount[getService('logement').Id] > 0">
                <!-- TODO: move hardcoded French text to text store -->
                <div class="bpa-front-module-heading">{{ venueHeading }}</div>
                <div class="bpa-front-module--service-items-row">
                  <card-item
                    :title="salleVenueTitle"
                    :description="salleVenueDescription"
                    :price="salleVenuePrice"
                    :isSelected="selectedVenue === 'lasalle'"
                    :disabled="!venueAvailable('lasalle')"
                    :hasQuantity="false"
                    :pictureUrl="salleVenuePicture"
                    v-model="amount['diner']"
                    @click="selectVenue('lasalle')"
                  ></card-item>
                  <card-item
                    v-if="getService('dinerlaserre') || getService('logement_serre')"
                    :title="serreVenueTitle"
                    :description="serreVenueDescription"
                    :price="serreVenuePrice"
                    :isSelected="selectedVenue === 'serre'"
                    :disabled="serreDisabled"
                    :hasQuantity="false"
                    :pictureUrl="serreVenuePicture"
                    v-model="amount['diner']"
                    @click="selectVenue('serre')"
                  ></card-item>
                </div>
              </span>
              <!---- Early heads-up: same-side noon service the morning after a
                     lodging stay → early checkout. Acknowledged on the summary
                     step; this is only an advance notice. -->
              <div
                v-if="noonNotice"
                class="noon-type-notice"
                data-testid="noon-type-notice"
              >
                En raison d'une réservation prévue le lendemain à midi, le
                logement devra être libéré au plus tard à 10h30. Si vous
                sélectionnez l'option brunch dans les étapes suivantes, celui-ci
                vous sera livré au plus tard à 9h00 afin que vous puissiez en
                profiter avant votre départ.
              </div>
              <!---- Choice of table or full room (lasalle venue only) -->
              <span v-if="amount[getService('diner').Id] > 0 && selectedVenue === 'lasalle'">
                <div class="bpa-front-module-heading">Le dîner</div>
                <div class="bpa-front-module--service-items-row">
                  <card-item
                    :title="getService('dinerlatable').Titre"
                    :description="getService('dinerlatable').Description"
                    :price="getService('dinerlatable').Prix"
                    :disabled="(amount['diner'] < serviceMinimum('dinerlatable') || amount['diner'] > roueMaxValue) && !combinedSpaces"
                    :pictureUrl="getService('dinerlatable').Photo"
                    v-model="amount[getService('dinerlatable').Id]"
                    :isSelected="selectedDiner == 'dinerlatable' || combinedSpaces"
                    :hasQuantity="false"
                    @click="selectDiner(getService('dinerlatable').Id)"
                  ></card-item>
                  <card-item
                    v-if="amount['diner'] >= salleMinValue"
                    :title="getService('dinerlasalle').Titre"
                    :description="getService('dinerlasalle').Description"
                    :price="getService('dinerlasalle').Prix"
                    :disabled="amount['diner'] < salleMinValue"
                    :pictureUrl="getService('dinerlasalle').Photo"
                    v-model="amount[getService('dinerlasalle').Id]"
                    :isSelected="selectedDiner == 'dinerlasalle'"
                    :hasQuantity="false"
                    @click="selectDiner(getService('dinerlasalle').Id)"
                  ></card-item>
                </div>
              </span>
              <p v-if="combinedSpaces" data-testid="combined-spaces-info">
                {{ combinedSpacesMessage }}
              </p>
              <!---- Choice of room disposition (lasalle venue only) -->
              <span v-if="selectedVenue === 'lasalle' && selectedDiner == 'dinerlasalle'">
                <div class="bpa-front-module-heading">
                  Disposition des tables
                </div>
                <div class="bpa-front-module--service-items-row">
                  <card-item
                    :title="getService('dinerlasalletables').Titre"
                    :description="getService('dinerlasalletables').Description"
                    :disabled="!separateTablesAvailable"
                    :pictureUrl="getService('dinerlasalletables').Photo"
                    v-model="amount[getService('dinerlasalletables').Id]"
                    :isSelected="selectedDisposition == 'dinerlasalletables'"
                    :hasQuantity="false"
                    @click="
                      selectDisposition(getService('dinerlasalletables').Id)
                    "
                  ></card-item>
                  <card-item
                    :title="getService('dinerlasalleovale').Titre"
                    :description="getService('dinerlasalleovale').Description"
                    :disabled="!ovalAvailable"
                    :pictureUrl="getService('dinerlasalleovale').Photo"
                    v-model="amount[getService('dinerlasalleovale').Id]"
                    :isSelected="selectedDisposition == 'dinerlasalleovale'"
                    :hasQuantity="false"
                    @click="
                      selectDisposition(getService('dinerlasalleovale').Id)
                    "
                  ></card-item>
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bpa-front-dc--footer">
        <div class="el-row">
          <div class="el-col el-col-24">
            <div class="bpa-front-tabs--foot footer-eauget">
              <subtotal></subtotal>
              <!---->
              <button
                type="button"
                @click="$emit('previousStep')"
                style="margin-left: auto"
                class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--borderless el-button--default"
                data-testid="btn-previous"
              >
                <!----><!----><span
                  ><svg
                    xmlns="http://www.w3.org/2000/svg"
                    enable-background="new 0 0 24 24"
                    viewBox="0 0 24 24"
                  >
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M9.7,18.3L9.7,18.3c0.39-0.39,0.39-1.02,0-1.41L5.83,13H21c0.55,0,1-0.45,1-1v0c0-0.55-0.45-1-1-1H5.83l3.88-3.88 c0.39-0.39,0.39-1.02,0-1.41l0,0c-0.39-0.39-1.02-0.39-1.41,0L2.7,11.3c-0.39,0.39-0.39,1.02,0,1.41l5.59,5.59 C8.68,18.68,9.32,18.68,9.7,18.3z"
                    ></path>
                  </svg>
                  Retour
                </span></button
              ><button
                v-if="!bookingData.canGoMenu() && !bookingData.canGoOptions()"
                type="button"
                @click="$emit('nextStep')"
                :disabled="!canGoNext()"
                :class="{ 'is-disabled': !canGoNext() }"
                class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default eauget-next-step"
                data-testid="btn-next"
              >
                <!----><!----><span>
                  Suivant&nbsp;<strong></strong>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enable-background="new 0 0 24 24"
                    viewBox="0 0 24 24"
                  >
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M14.29,5.71L14.29,5.71c-0.39,0.39-0.39,1.02,0,1.41L18.17,11H3c-0.55,0-1,0.45-1,1v0c0,0.55,0.45,1,1,1h15.18l-3.88,3.88 c-0.39,0.39-0.39,1.02,0,1.41l0,0c0.39,0.39,1.02,0.39,1.41,0l5.59-5.59c0.39-0.39,0.39-1.02,0-1.41L15.7,5.71 C15.32,5.32,14.68,5.32,14.29,5.71z"
                    ></path></svg></span></button
              ><button
                v-if="bookingData.canGoMenu()"
                type="button"
                @click="$emit('nextStep')"
                :disabled="!canGoNext()"
                :class="{ 'is-disabled': !canGoNext() }"
                class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default eauget-next-step"
                data-testid="btn-next"
              >
                <!----><!----><span>
                  Suivant :&nbsp;<strong>Nombre de services</strong>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enable-background="new 0 0 24 24"
                    viewBox="0 0 24 24"
                  >
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M14.29,5.71L14.29,5.71c-0.39,0.39-0.39,1.02,0,1.41L18.17,11H3c-0.55,0-1,0.45-1,1v0c0,0.55,0.45,1,1,1h15.18l-3.88,3.88 c-0.39,0.39-0.39,1.02,0,1.41l0,0c0.39,0.39,1.02,0.39,1.41,0l5.59-5.59c0.39-0.39,0.39-1.02,0-1.41L15.7,5.71 C15.32,5.32,14.68,5.32,14.29,5.71z"
                    ></path></svg></span></button
              ><button
                v-if="!bookingData.canGoMenu() && bookingData.canGoOptions()"
                type="button"
                @click="$emit('nextStep')"
                :disabled="!canGoNext()"
                :class="{ 'is-disabled': !canGoNext() }"
                class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default eauget-next-step"
                data-testid="btn-next"
              >
                <!----><!----><span>
                  Suivant :&nbsp;<strong>Options</strong>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enable-background="new 0 0 24 24"
                    viewBox="0 0 24 24"
                  >
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M14.29,5.71L14.29,5.71c-0.39,0.39-0.39,1.02,0,1.41L18.17,11H3c-0.55,0-1,0.45-1,1v0c0,0.55,0.45,1,1,1h15.18l-3.88,3.88 c-0.39,0.39-0.39,1.02,0,1.41l0,0c0.39,0.39,1.02,0.39,1.41,0l5.59-5.59c0.39-0.39,0.39-1.02,0-1.41L15.7,5.71 C15.32,5.32,14.68,5.32,14.29,5.71z"
                    ></path></svg></span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="bpa-fm--service__advance-options">
        <div class="bpa-sao--body">
          <!---->
          <!---->
        </div>
        <div class="bpa-sao--footer">
          <button
            type="button"
            class="el-button bpa-front-btn bpa-front-btn--borderless bpa-front-btn__medium el-button--default"
          >
            <!----><!----><span>Annuler</span>
          </button>
          <button
            type="button"
            class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"
          >
            <!----><!----><span>Continuer</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.noon-type-notice {
  margin: 12px 0 4px;
  padding: 10px 14px;
  border-left: 3px solid #c8a24a;
  background: #faf6ec;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.35;
  color: #5a4a1f;
}
</style>
